import * as React from 'react';
import { useRecoilValue } from 'recoil';
import {
  IRide,
  IRideAlarmLinePaths,
  IRideMapAlarmLines,
  IRidePosition,
  TRideActiveLayer,
} from '../types';
import { IS_ALARM_LAYER } from '../constants';
import { activeRideNumberAtom, selectedRidesAtom } from '../RidePlayerState';
import { createAlarmLines, updateAlarmLines } from '../RidePlayerService';

interface IUseAlarmLine {
  alarmLines: React.MutableRefObject<IRideMapAlarmLines>;
  setAlarmRideLinesSize: (strokeWeight: number) => void;
  showAllAlarmLines: () => void;
  showAlarmLine: (rideNumber: number) => void;
  setAlarmLinesOpacity: (rideNumber: number, opacity: number) => void;
  setAlarmLineZIndex: (rideNumber: number, zIndex: number) => void;
  hideAllAlarmLines: () => void;
  hideAlarmLine: (rideNumber: number) => void;
  setAllAlarmLinesColor: (color: string, exceptRideNumber?: number) => void;
  removeAllAlarmLines: () => void;
  createAlarmLines: () => void;
  resetAllAlarmLinesColor: () => void;
  resetAlarmLinesColorForRide: (rideNumber: number) => void;
  setAllAlarmLinesZIndex: (zIndex: number, exceptRideNumber?: number) => void;
}

/**
 * useAlarmLine custom hook
 * @param {google.maps.Map} map
 * @returns {IUseAlarmLine}
 */
const useAlarmLine = (
  map: google.maps.Map,
  rides: IRide[],
  activeLayer: TRideActiveLayer,
  actualRidePosition?: IRidePosition | null
): IUseAlarmLine => {
  const alarmLines = React.useRef<IRideMapAlarmLines>({});
  const alarmLinePaths = React.useRef<IRideAlarmLinePaths>({});
  const selectedRides = useRecoilValue<number[]>(selectedRidesAtom);
  const activeRide = useRecoilValue<number>(activeRideNumberAtom);

  /**
   * Set alarm lines stroke weight (aka size)
   * @param {number} strokeWeight
   * @returns {void}
   */
  const setAlarmRideLinesSize = (strokeWeight = 2): void => {
    Object.keys(alarmLines.current).forEach((rideNumber: string) => {
      const polylines = alarmLines.current[rideNumber];
      polylines.forEach((polyline: any) => {
        if (polyline.strokeWeight !== strokeWeight) {
          polyline.setOptions({ strokeWeight });
        }
      });
    });
  };

  /**
   * Show alarm line
   * @param {number} rideNumber
   * @return {void}
   */
  const showAlarmLine = (rideNumber: number): void => {
    if (!alarmLines.current[rideNumber]) {
      console.warn(`showAlarmLine: ride ${rideNumber} not exist`);
    }
    const polylines: google.maps.Polyline[] = alarmLines.current[rideNumber];
    polylines.forEach((polyline: google.maps.Polyline) => {
      if (!polyline.getMap()) {
        polyline.setMap(map);
      }
    });
  };

  /**
   * Hide alarm line
   * @param {number} rideNumber
   * @return {void}
   */
  const hideAlarmLine = (rideNumber: number): void => {
    if (!alarmLines.current[rideNumber]) {
      console.warn(`hideAlarmLine: ride ${rideNumber} not exist`);
    }
    const polylines: google.maps.Polyline[] = alarmLines.current[rideNumber];
    polylines.forEach((polyline: google.maps.Polyline) => {
      if (polyline.getMap()) {
        polyline.setMap(null);
      }
    });
  };

  /**
   * Show all alarm lines
   * @returns {void}
   */
  const showAllAlarmLines = (): void => {
    Object.keys(alarmLines.current).forEach((rideNumber: string) => {
      const polylines: google.maps.Polyline[] = alarmLines.current[Number(rideNumber)];
      polylines.forEach((polyline: google.maps.Polyline) => {
        if (!polyline.getMap() && selectedRides.includes(Number(rideNumber))) {
          polyline.setMap(map);
        }
      });
    });
  };

  /**
   * Hide all alarm lines
   * @returns {void}
   */
  const hideAllAlarmLines = (): void => {
    Object.keys(alarmLines.current).forEach((rideNumber: string) => {
      const polylines: google.maps.Polyline[] = alarmLines.current[Number(rideNumber)];
      polylines.forEach((polyline: google.maps.Polyline) => {
        if (polyline.getMap()) {
          polyline.setMap(null);
        }
      });
    });
  };

  /**
   * Set all alarm lines color
   * @param {string} color
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllAlarmLinesColor = (color: string, exceptRideNumber?: number): void => {
    if (alarmLines.current) {
      Object.keys(alarmLines.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        const polylines: google.maps.Polyline[] = alarmLines.current[rideNumber];
        polylines.forEach((polyline: google.maps.Polyline) => {
          polyline.setOptions({ strokeColor: color });
        });
      });
    }
  };

  /**
   * Set alarm line opacity for specified ride
   * @param {number} rideNumber
   * @param {number} opacity
   */
  const setAlarmLinesOpacity = (rideNumber: number, opacity: number): void => {
    if (alarmLines.current && alarmLines.current[rideNumber]) {
      const polylines = alarmLines.current[String(rideNumber)];
      polylines.forEach((polyline: any) => {
        if (polyline && polyline.strokeOpacity) {
          polyline.setOptions({ strokeOpacity: opacity });
        }
      });
    }
  };

  /**
   * Set alarm line zindex for specified ride
   * @param {number} rideNumber
   * @param {number} zIndex
   * @returns {void}
   */
  const setAlarmLineZIndex = (rideNumber: number, zIndex: number): void => {
    if (alarmLines.current && alarmLines.current[rideNumber]) {
      const polylines = alarmLines.current[String(rideNumber)];
      polylines.forEach((polyline: any) => {
        if (polyline && polyline.strokeOpacity) {
          polyline.setOptions({ zIndex });
        }
      });
    }
  };

  /**
   * Set all alarm lines zIndex
   * @param {number} zIndex
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllAlarmLinesZIndex = (zIndex: number, exceptRideNumber?: number): void => {
    if (alarmLines.current) {
      Object.keys(alarmLines.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        const polylines: google.maps.Polyline[] = alarmLines.current[rideNumber];
        polylines.forEach((polyline: google.maps.Polyline) => {
          polyline.setOptions({ zIndex });
        });
      });
    }
  };

  /**
   * Remove all alarm lines from map
   * @returns {void}
   */
  const removeAllAlarmLines = (): void => {
    hideAllAlarmLines();
    alarmLines.current = {};
  };

  /**
   * Create alarm lines
   * @returns {void}
   */
  const createRideAlarmLines = (): void => {
    const showOnMap = activeLayer === IS_ALARM_LAYER;
    alarmLines.current = createAlarmLines(
      showOnMap ? map : null,
      rides,
      selectedRides,
      alarmLinePaths.current
    );
  };

  /**
   * Reset all alarm lines color for all rides
   * @returns {void}
   */
  const resetAllAlarmLinesColor = (): void => {
    Object.keys(alarmLines.current).forEach((rideNumber: string) => {
      const polylines: google.maps.Polyline[] = alarmLines.current[rideNumber];
      polylines.forEach((polyline: google.maps.Polyline) => {
        const originalColor = polyline.get('originalColor');
        polyline.setOptions({ strokeColor: originalColor });
      });
    });
  };

  /**
   * Reset alarm lines color for specified ride
   * @param {number} rideNumber
   * @returns {void}
   */
  const resetAlarmLinesColorForRide = (rideNumber: number): void => {
    if (alarmLines.current[rideNumber]) {
      const polylines: google.maps.Polyline[] = alarmLines.current[rideNumber];
      polylines.forEach((polyline: google.maps.Polyline) => {
        const originalColor = polyline.get('originalColor');
        polyline.setOptions({ strokeColor: originalColor });
      });
    }
  };

  // Handle selected rides
  React.useEffect(() => {
    Object.keys(alarmLines.current).forEach((rideNumber: string) => {
      alarmLines.current[Number(rideNumber)].forEach((polyline: google.maps.Polyline) => {
        if (!selectedRides.includes(Number(rideNumber)) && polyline.getMap()) {
          polyline.setMap(null);
        }
        if (
          selectedRides.includes(Number(rideNumber)) &&
          !polyline.getMap() &&
          activeLayer === IS_ALARM_LAYER
        ) {
          polyline.setMap(map);
        }
      });
    });
  }, [activeLayer, map, selectedRides]);

  // Create alarm lines on load
  React.useEffect(() => {
    createRideAlarmLines();

    // Unset alarm lines from map
    return () => {
      hideAllAlarmLines();
      alarmLines.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If actual position is present, update speedlimit lines
  React.useEffect(() => {
    if (actualRidePosition && activeRide) {
      updateAlarmLines(
        activeLayer === IS_ALARM_LAYER ? map : undefined,
        rides,
        alarmLines.current,
        alarmLinePaths.current,
        activeRide,
        actualRidePosition
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualRidePosition, activeRide]);

  const data: IUseAlarmLine = {
    alarmLines,
    setAlarmRideLinesSize,
    showAllAlarmLines,
    setAlarmLinesOpacity,
    setAlarmLineZIndex,
    hideAllAlarmLines,
    showAlarmLine,
    hideAlarmLine,
    setAllAlarmLinesColor,
    removeAllAlarmLines,
    createAlarmLines: createRideAlarmLines,
    resetAllAlarmLinesColor,
    resetAlarmLinesColorForRide,
    setAllAlarmLinesZIndex,
  };

  return data;
};

export default useAlarmLine;
